(() => {
  const ENTRY_KEY = 'pupsEntryProduct:v1';
  const SESSION_KEY = 'pupsEntrySession:v1';

  const normalizeHandle = (value) => {
    if (!value) return '';
    let handle = String(value).trim().toLowerCase();
    for (let pass = 0; pass < 2 && /%[0-9a-f]{2}/i.test(handle); pass += 1) {
      try {
        handle = decodeURIComponent(handle);
      } catch {
        return '';
      }
    }
    return /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle) ? handle : '';
  };

  const normalizeVariant = (value) => {
    const variant = value ? String(value) : '';
    return /^\d{6,20}$/.test(variant) ? variant : '';
  };

  const normalizeCollection = (value) => normalizeHandle(value);

  const readStorage = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const writeStorage = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  };

  const readEntry = () => {
    const stored = readStorage(ENTRY_KEY);
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored);
      const handle = normalizeHandle(parsed.handle);
      if (!handle) return null;
      return {
        handle,
        variant: normalizeVariant(parsed.variant),
        collectionHandle: normalizeCollection(parsed.collectionHandle),
      };
    } catch {
      return null;
    }
  };

  const saveEntry = (entry) => {
    const handle = normalizeHandle(entry && entry.handle);
    if (!handle) return null;
    const normalized = {
      handle,
      variant: normalizeVariant(entry.variant),
      collectionHandle: normalizeCollection(entry.collectionHandle),
    };
    writeStorage(ENTRY_KEY, JSON.stringify(normalized));
    return normalized;
  };

  const script = document.querySelector('script[data-pups-entry-product]');
  const params = new URLSearchParams(window.location.search);
  const explicitHandle = normalizeHandle(params.get('first'));
  const explicitVariant = normalizeVariant(params.get('variant'));
  const pageHandle = normalizeHandle(script && script.dataset.productHandle);
  const pageVariant = normalizeVariant(script && script.dataset.variantId);
  const isProductPage = (script && script.dataset.templateName) === 'product' && pageHandle;
  let entry = readEntry();

  if (explicitHandle) {
    entry = saveEntry({
      handle: explicitHandle,
      variant: explicitVariant || (entry && entry.handle === explicitHandle ? entry.variant : ''),
      collectionHandle: entry && entry.handle === explicitHandle ? entry.collectionHandle : '',
    });
    writeStorage(SESSION_KEY, 'true');
  } else if (!readStorage(SESSION_KEY)) {
    if (isProductPage) {
      entry = saveEntry({ handle: pageHandle, variant: explicitVariant || pageVariant, collectionHandle: '' });
    }
    writeStorage(SESSION_KEY, 'true');
  }

  const getCollectionHandle = (urlValue) => {
    try {
      const url = new URL(urlValue, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      const parts = url.pathname.split('/').filter(Boolean);
      return parts[0] === 'collections' ? normalizeCollection(parts[1]) : '';
    } catch {
      return '';
    }
  };

  const decorateUrl = (urlValue) => {
    const current = readEntry() || entry;
    if (!current) return urlValue;
    try {
      const url = new URL(urlValue, window.location.origin);
      if (url.origin !== window.location.origin) return urlValue;
      url.searchParams.set('first', current.handle);
      if (current.variant) url.searchParams.set('variant', current.variant);
      return url.href;
    } catch {
      return urlValue;
    }
  };

  const getPrimaryProductInfo = () =>
    document.querySelector('product-info[id^="MainProduct-"]') ||
    document.querySelector('main product-info') ||
    document.querySelector('product-info');

  const getProductHandle = (productInfo) => {
    try {
      const productUrl = new URL(productInfo?.dataset.url || window.location.href, window.location.origin);
      const parts = productUrl.pathname.split('/').filter(Boolean);
      const productIndex = parts.lastIndexOf('products');
      return productIndex === -1 ? '' : normalizeHandle(parts[productIndex + 1]);
    } catch {
      return '';
    }
  };

  const getProductVariant = (productInfo, variantValue) => {
    if (variantValue !== undefined) return normalizeVariant(variantValue);

    try {
      const urlVariant = normalizeVariant(new URL(window.location.href).searchParams.get('variant'));
      if (urlVariant) return urlVariant;
    } catch {}

    const formVariant = normalizeVariant(
      productInfo?.querySelector('form[action*="/cart/add"] input[name="id"]')?.value,
    );
    return formVariant || pageVariant;
  };

  const getSelectedColor = (productInfo) => {
    const colorFieldset = [...(productInfo?.querySelectorAll('variant-selects fieldset') || [])].find(
      (fieldset) => fieldset.querySelector('input:checked')?.name.toLowerCase().startsWith('color'),
    );
    const color = colorFieldset?.querySelector('input:checked')?.value?.trim() || '';
    return color.length <= 100 ? color : '';
  };

  const resolveColorVariant = (productInfo, color) => {
    const productGroup = [...(productInfo?.querySelectorAll('script[type="application/ld+json"]') || [])]
      .map((scriptElement) => {
        try {
          return JSON.parse(scriptElement.textContent);
        } catch {
          return null;
        }
      })
      .find((item) => item?.['@type'] === 'ProductGroup' && Array.isArray(item.hasVariant));
    if (!productGroup) return '';
    const nameSuffix = ` - ${color} /`.toLowerCase();
    const matches = productGroup.hasVariant.filter((candidate) =>
      String(candidate?.name || '').toLowerCase().includes(nameSuffix),
    );
    const variant =
      matches.find((candidate) => {
        const offers = Array.isArray(candidate.offers) ? candidate.offers[0] : candidate.offers;
        return String(offers?.availability || '').endsWith('/InStock');
      }) || matches[0];
    const offers = Array.isArray(variant?.offers) ? variant.offers[0] : variant?.offers;
    const variantUrl = offers?.url || variant?.['@id'];
    try {
      return normalizeVariant(new URL(variantUrl, window.location.origin).searchParams.get('variant'));
    } catch {
      return '';
    }
  };

  const writeProductBreedLinks = (root, handle, variant) => {
    root
      .querySelectorAll(
        '.pups-breed-link a[href*="/collections/"], .pups-product-breadcrumbs a[href*="/collections/"]',
      )
      .forEach((link) => {
        try {
          const url = new URL(link.href, window.location.origin);
          if (url.origin !== window.location.origin) return;
          url.searchParams.set('first', handle);
          if (variant) url.searchParams.set('variant', variant);
          else url.searchParams.delete('variant');
          link.href = url.href;
        } catch {}
      });
  };

  const decorateProductBreedLinks = (scope = document, variantValue) => {
    if (!isProductPage) return;
    const productInfo = getPrimaryProductInfo();
    const handle = getProductHandle(productInfo) || pageHandle;
    if (!handle) return;
    let variant = getProductVariant(productInfo, variantValue);
    const color = getSelectedColor(productInfo);
    const root = scope?.querySelectorAll ? scope : document;
    if (!variant && color) variant = resolveColorVariant(productInfo, color);
    writeProductBreedLinks(root, handle, variant);
  };

  window.PupsEntryProduct = {
    get: () => readEntry() || entry,
    save: (value) => {
      entry = saveEntry(value);
      return entry;
    },
    decorateUrl,
    normalizeHandle,
    normalizeVariant,
    normalizeCollection,
  };

  const rewriteProductLinks = (container, current) => {
    container.querySelectorAll('a[href]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        const parts = url.pathname.split('/').filter(Boolean);
        const productIndex = parts.lastIndexOf('products');
        if (productIndex === -1 || parts[productIndex + 1] !== current.handle) return;
        url.searchParams.set('first', current.handle);
        if (current.variant) url.searchParams.set('variant', current.variant);
        link.href = url.href;
      } catch {
        // Leave malformed links unchanged.
      }
    });
    container.querySelectorAll('[data-product-url]').forEach((element) => {
      try {
        const url = new URL(element.dataset.productUrl, window.location.origin);
        url.searchParams.set('first', current.handle);
        if (current.variant) url.searchParams.set('variant', current.variant);
        element.dataset.productUrl = `${url.pathname}${url.search}`;
      } catch {
        // Leave malformed data URLs unchanged.
      }
    });
  };

  const promoteCard = (grid, card, current, originalCount) => {
    const familyKey = card.dataset.pupsFamilyKey || '';
    [...grid.children]
      .filter(
        (candidate) =>
          candidate !== card &&
          (candidate.dataset.pupsCuratedHandle === current.handle ||
            candidate.dataset.pupsProductHandle === current.handle ||
            (familyKey && candidate.dataset.pupsFamilyKey === familyKey)),
      )
      .forEach((duplicate) => duplicate.remove());
    card.dataset.pupsCuratedHandle = current.handle;
    card.dataset.pupsEntryPinned = 'true';
    card.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
    card.style.removeProperty('--animation-order');
    rewriteProductLinks(card, current);
    const image = card.querySelector('img');
    if (image) {
      image.loading = 'eager';
      image.fetchPriority = 'high';
    }
    if (grid.firstElementChild !== card) grid.prepend(card);
    while (grid.children.length > originalCount) grid.lastElementChild.remove();
  };

  const fetchEntryCard = async (current) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 5000);
    const sourceCollection = current.collectionHandle || 'shop-all-tees';
    const url = new URL(
      `/collections/${encodeURIComponent(sourceCollection)}/products/${encodeURIComponent(current.handle)}`,
      window.location.origin,
    );
    url.searchParams.set('view', 'pups-ad-card');
    if (current.variant) url.searchParams.set('variant', current.variant);
    ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
      if (params.has(key)) url.searchParams.set(key, params.get(key));
    });

    try {
      const response = await fetch(url.href, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) return null;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const wrapper = html.querySelector('[data-pups-ad-card]');
      if (!wrapper || wrapper.dataset.pupsAdCard !== current.handle) return null;
      const card = wrapper.querySelector('li[data-pups-product-handle]');
      return card && card.dataset.pupsProductHandle === current.handle ? document.importNode(card, true) : null;
    } catch {
      return null;
    } finally {
      window.clearTimeout(timer);
    }
  };

  const personalizePackFavorites = async (scope = document) => {
    const current = readEntry() || entry;
    if (!current) return;
    const section = scope.querySelector('[data-pups-entry-slot]') ||
      (scope.matches && scope.matches('[data-pups-entry-slot]') ? scope : null);
    if (!section || section.dataset.pupsEntryReady === 'true') return;
    section.dataset.pupsEntryReady = 'true';
    const grid = section.querySelector('.product-grid');
    if (!grid || grid.children.length === 0) return;
    const originalCount = grid.children.length;
    const existing = [...grid.children].find(
      (card) =>
        card.dataset.pupsCuratedHandle === current.handle || card.dataset.pupsProductHandle === current.handle,
    );
    if (existing) {
      promoteCard(grid, existing, current, originalCount);
      return;
    }
    const fetched = await fetchEntryCard(current);
    if (fetched) promoteCard(grid, fetched, current, originalCount);
  };

  const initialize = (scope = document) => {
    if (isProductPage && entry && entry.handle === pageHandle && !entry.collectionHandle) {
      const breedLink = scope.querySelector('.pups-breed-link a[href*="/collections/"]') ||
        scope.querySelector('.pups-product-breadcrumbs a[href*="/collections/"]');
      const collectionHandle = breedLink ? getCollectionHandle(breedLink.href) : '';
      if (collectionHandle && !collectionHandle.startsWith('shop-all')) {
        entry = saveEntry({ ...entry, collectionHandle });
      }
    }

    const current = readEntry() || entry;
    if (!current) return;
    scope.querySelectorAll('a[href]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        const collectionHandle = getCollectionHandle(url.href);
        if (url.pathname === '/') {
          link.href = decorateUrl(url.href);
        } else if (current.collectionHandle && collectionHandle === current.collectionHandle) {
          link.href = decorateUrl(url.href);
        }
      } catch {
        // Leave malformed links unchanged.
      }
    });
    decorateProductBreedLinks(scope);
    personalizePackFavorites(scope);
    document.dispatchEvent(new CustomEvent('pups:entry-product-ready'));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('product-info:loaded', (event) => {
    if (event.target === getPrimaryProductInfo()) decorateProductBreedLinks(event.target);
  });
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('variant-selects input[data-option-name="Color"]')) {
      return;
    }
    const productInfo = input.closest('product-info');
    if (productInfo === getPrimaryProductInfo()) decorateProductBreedLinks(productInfo, null);
  });
  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined' &&
    PUB_SUB_EVENTS.variantChange
  ) {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
      const productInfo = getPrimaryProductInfo();
      const sectionId = productInfo?.dataset.originalSection || productInfo?.dataset.section;
      if (data?.sectionId && sectionId && data.sectionId !== sectionId) return;
      decorateProductBreedLinks(productInfo || document, data?.variant ? data.variant.id : null);
    });
  }
})();
